import { RouteComponentProps } from 'react-router';
import { withTranslation, WithTranslation } from 'react-i18next';
import {
  adminModuleEditRoles,
  NotificationModel,
  NotificationRequestModel,
  SupplierModel
} from '../../../../api/admin/admin.types';
import { UserModel } from '../../../../api/authentication/authentication.types';
import React, { FormEvent } from 'react';
import { AppState } from '../../../../store/store.types';
import { ThunkDispatch } from 'redux-thunk';
import { connect } from 'react-redux';
import {
  addNewNotification,
  deleteNotification,
  editNotification,
  getNotification
} from '../../actions/notification.actions';
import { NotificationActionTypes } from '../../types/notification.types';
import Breadcrumbs from '../../../../components/breadcrumbs/Breadcrumbs';
import { FieldControl, FieldGroup, FormBuilder, Validators } from 'react-reactive-form';
import { userCanAccessComponent } from '../../../../helpers/authentication.helper';
import SectionTitle from '../../../../components/forms/SectionTitle';
import { formatDate } from '../../../../helpers/date.helper';
import { TextInput } from '../../../../components/forms/Input';
import DatePicker from '../../../../components/forms/DatePicker';
import Checkbox from '../../../../components/forms/Checkbox';
import { TwoColumnsSelectValueModel } from '../../../../components/forms/TwoColumnsSelect';
import RadioButton from '../../../../components/forms/RadioButton';
import { MultiSelect } from '../../../../components/forms/MultiSelect';
import ConfirmationModal from '../../../../components/modals/ConfirmationModal';
import { InputTextArea } from '../../../../components/forms/TextArea';
import Markdown from 'markdown-to-jsx';
import dayjs from 'dayjs';

interface NotificationLocationProps {
  id: string;
}

interface NotificationDetailProps extends RouteComponentProps<NotificationLocationProps>, WithTranslation {
  selectedNotification: NotificationModel | null;
  getNotification: (id: number) => Promise<void>;
  addNotification: (request: NotificationRequestModel) => void;
  editNotification: (request: NotificationRequestModel, id: number) => void;
  deleteNotification: (id: number) => Promise<void>;
  isNew?: boolean;
  supplier: SupplierModel | null;
  supplierList: SupplierModel[];
  postSuccess: boolean | null;
  lastFetch: number | null;
  user: UserModel | null;
}

interface NotificationDetailState {
  isEdit: boolean;
  selectedSuppliers: string[];
  isModalOpen: boolean;
}

class NotificationDetails extends React.Component<NotificationDetailProps, NotificationDetailState> {

  readonly state = {
    isEdit: false,
    selectedSuppliers: [],
    isModalOpen: false
  };

  form = FormBuilder.group({
    title: [ null, [ Validators.required ] ],
    text: [ null, [ Validators.required ] ],
    expiresAfter: [ null ],
    important: [ false ],
    supplierSelection: [ 'all' ],
    suppliers: [ { items: [], selected: [] } ]
  });

  async componentDidMount() {
    const { id } = this.props.match.params;

    if (id) {
      await this.props.getNotification(Number(id));
    }

    if (this.props.supplierList.length) {
      this.assignSuppliersToForm(this.props.supplierList);
    }

  }

  componentDidUpdate(prevProps: NotificationDetailProps, prevState: NotificationDetailState) {
    if ((this.props.isNew) && this.props.postSuccess && this.props.selectedNotification) {
      this.props.history.push(`/admin/notifications/${this.props.selectedNotification.id}/view`);
    }

    if (this.props.selectedNotification && (!prevProps.selectedNotification || this.props.selectedNotification !== prevProps.selectedNotification)) {
      this.updateFormValue();
    }

    if (this.props.supplierList.length !== prevProps.supplierList.length) {
      this.assignSuppliersToForm(this.props.supplierList);
    }

  }

  updateFormValue = () => {
    if (!this.props.selectedNotification) {
      return;
    }
    this.form.patchValue({
      title: this.props.selectedNotification.title,
      text: this.props.selectedNotification.text,
      expiresAfter: this.props.selectedNotification.expiresAfter ? dayjs(this.props.selectedNotification.expiresAfter) : null,
      important: this.props.selectedNotification.important,
      supplierSelection: this.props.selectedNotification.suppliers.length ? 'selection' : 'none',
      suppliers: this.props.selectedNotification ?
        {
          ...this.form.controls.suppliers.value,
          selected: this.props.selectedNotification.suppliers.map((supplier: { code: string }) => supplier.code)
        } : this.form.controls.suppliers.value
    });
    this.setState({
      selectedSuppliers: this.props.selectedNotification.suppliers.map((s) => s.code)
    });
  };


  notificationType = (important: boolean) => {
    return (
      <div className={important ? 'text-warning' : 'text-success'}>
        {important ? 'IMPORTANT' : 'INFORMATIVE'}
      </div>
    );
  };

  onDeleteConfirm = async () => {
    const { id } = this.props.match.params;
    await this.props.deleteNotification(Number(id));
    this.props.history.push('/admin/notifications');
  };

  assignSuppliersToForm = (storedSuppliers: SupplierModel[]) => {
    const form = this.form.controls.suppliers.value as TwoColumnsSelectValueModel;
    storedSuppliers.forEach((p, inx) => {
      const obj = { code: p.code, name: p.name, id: inx };
      form.items.push(obj);
    });

    if (this.props.selectedNotification) {
      this.props.selectedNotification.suppliers.forEach((supplier) => {
        form.selected.push(supplier.code);
      });
      this.setState({
        selectedSuppliers: this.props.selectedNotification.suppliers.map((p) => p.code)
      });
    }
  };

  getSuppliers = (suppliers: { code: string }[]) => {
    const supplierElms: string[] = [];
    let supplierList;
    if (suppliers.length === 0) {
      supplierList = 'No suppliers';
    } else {
      suppliers.forEach((supplierCode) => {
        const supplier = this.props.supplierList.find((i) => i.code === supplierCode.code);
        if (supplier && !supplierElms.includes(supplier.name)) {
          supplierElms.push(supplier.name);
        }

      });

      // eslint-disable-next-line no-nested-ternary
      supplierElms.sort((a, b) => ((a < b) ? -1 : (a > b) ? 1 : 0));
      supplierList = (
        <ul className="details-list">
          {supplierElms.map((supplier) => (
            <li key={supplier}>{supplier}</li>
          ))}
        </ul>
      );
    }
    return supplierList;
  };

  onAllSuppliersSelect = () => {
    this.form.controls.suppliers.patchValue({ ...this.form.controls.suppliers.value, selected: [] });
    this.setState({ selectedSuppliers: [] });
  };

  handleSupplierValueChange = (v: any) => {
    this.setState({
      selectedSuppliers: v.map((itm: any) => {
        return itm.code;
      })
    });
  };

  onCancel = () => {
    if (this.props.isNew) {
      this.props.history.push('/admin/notifications/');
    } else {
      this.updateFormValue();
      this.setState({ isEdit: false });
    }
  };

  onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const form = this.form.controls;
    this.form.markAsSubmitted();
    if (this.form.invalid) {
      return;
    }
    if (!this.props.isNew && this.props.selectedNotification) {
      const request: NotificationRequestModel = {
        ...this.props.selectedNotification,
        title: form.title.value,
        text: form.text.value,
        expiresAfter: form.expiresAfter.value,
        important: form.important.value,
        suppliers: this.state.selectedSuppliers
      };
      const { id } = this.props.match.params;

      this.props.editNotification(request, Number(id));
      this.setState({ isEdit: false });
    } else {
      const request: NotificationRequestModel = {
        title: form.title.value,
        text: form.text.value,
        expiresAfter: form.expiresAfter.value,
        important: form.important.value,
        suppliers: this.state.selectedSuppliers
      };

      this.props.addNotification(request);
    }
  };


  getDetailView = (showForm: boolean) => {
    const { t } = this.props;
    return (!showForm && this.props.selectedNotification ? (
      <>
        <div className="d-flex">
          <div className="col-12">
            <div className="d-flex row info-container">
              <label
                className="col-4 d-flex align-items-center justify-content-end">{t('l.notification.title')}: </label>
              <div
                className="col-4 d-flex align-items-center justify-content-start text-bold">{this.props.selectedNotification.title}</div>
            </div>
            <div className="d-flex row info-container">
              <label
                className="col-4 d-flex align-items-center justify-content-end">{t('l.notification.text')}: </label>
              <div className="col-6 d-flex align-items-center justify-content-start">
                <Markdown options={{ disableParsingRawHTML: true }}>{this.props.selectedNotification.text}</Markdown>
              </div>
            </div>
            <div className="d-flex row info-container">
              <label
                className="col-4 d-flex align-items-center justify-content-end">{t('l.notification.expires_after')}: </label>
              <div
                className="col-4 d-flex align-items-center justify-content-start">{this.props.selectedNotification.expiresAfter ? formatDate(this.props.selectedNotification.expiresAfter) : ''}</div>
            </div>
            <div className="d-flex row info-container">
              <label
                className="col-4 d-flex align-items-center justify-content-end">{t('l.notification.type')}: </label>
              <div
                className="col-4 d-flex align-items-center justify-content-start">{this.notificationType(this.props.selectedNotification.important)}</div>
            </div>
          </div>
        </div>
        <SectionTitle
          title={t('section_title.notification.suppliers').toUpperCase()}/>
        <div className="form-group d-flex">
          <div className="col-4"/>
          <div className="col-2 d-flex align-items-center justify-content-start">
            <div className="form-control-static">
              {this.getSuppliers(this.props.selectedNotification.suppliers)}
            </div>
          </div>
        </div>
      </>
    ) : '');
  };

  getEditView = (showForm: boolean) => {
    const { t } = this.props;
    return (showForm ? (
      <FieldGroup
        strict={false}
        control={this.form}
        render={() => {
          return (
            <form className="form-horizontal" onSubmit={(e) => this.onSubmit}>
              {showForm && this.props.user && userCanAccessComponent(this.props.user, adminModuleEditRoles) ? (
                <>
                  <div className="col-12">
                    <div className="form-group d-flex">
                      <div className="col-4 d-flex align-items-center justify-content-end">
                        <label className="mandatory-field">* </label>
                        <label>{t('l.notification.title')}: </label>
                      </div>
                      <div className="col-4 d-flex align-items-center justify-content-start edit-form-input wide-input">
                        <FieldControl
                          name="title"
                          render={TextInput}
                        />
                      </div>
                    </div>
                    <div className="form-group d-flex">
                      <div className="col-4 d-flex align-items-center justify-content-end">
                        <label className="mandatory-field">* </label>
                        <label>{t('l.notification.text')}: </label>
                      </div>
                      <div className="col-4 d-flex align-items-center justify-content-start edit-form-input wide-input">
                        <FieldControl
                          name="text"
                          render={InputTextArea}
                        />
                      </div>
                    </div>
                    <div className="form-group d-flex">
                      <div className="col-4 d-flex align-items-center justify-content-end">
                        <label className="mandatory-field">* </label>
                        <label>{t('l.notification.expires_after')}: </label>
                      </div>
                      <div className="col-4 d-flex row-gap">
                        <div className="col padding-zero">
                          <div className="date-picker-container d-flex align-items-end">
                            <DatePicker
                              id="expiresAfter"
                              isEdit
                              isInvalid={false}
                              date={this.form.controls.expiresAfter ? this.form.controls.expiresAfter.value : undefined}
                              onChange={(val) => {
                                this.form.patchValue({ expiresAfter: val });
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="form-group d-flex">
                      <div className="col-4 d-flex align-items-center justify-content-end">
                        <label>{t('l.notification.type')}:</label>
                      </div>
                      <div className="col-2 d-flex align-items-center justify-content-start">
                        <FieldControl
                          name="important"
                          render={(props) =>
                            <Checkbox {...props} id="importantField" className="mt-1">Important</Checkbox>
                          }
                        />
                      </div>
                    </div>
                  </div>
                  <SectionTitle title={t('section_title.notification.suppliers').toUpperCase()}/>
                  <div className="form-group row">
                    <div className="col-4"/>
                    <div className="col-6 d-flex align-items-center justify-content-start">
                      <div className="d-flex flex-column flex-wrap">
                        <div className="pb-1">
                          <FieldControl
                            strict={false}
                            name="supplierSelection"
                            render={(props) => (
                              <RadioButton
                                {...props}
                                onClick={this.onAllSuppliersSelect}
                                fieldValue="none"
                                id="sup_all"
                              >
                                {t('l.selection.none')}
                              </RadioButton>
                            )}
                          />
                          <FieldControl
                            strict={false}
                            name="supplierSelection"
                            render={(props) => (
                              <RadioButton
                                {...props}
                                fieldValue="selection"
                                id="sup_sel"
                              >
                                {t('l.selection.selected')}
                              </RadioButton>
                            )}
                          />
                        </div>
                        {
                          this.form.controls.supplierSelection.value === 'selection' && (
                            <FieldControl
                              name="suppliers"
                              render={(v) => (
                                <MultiSelect
                                  {...v}
                                  selectedAttributeName="code"
                                  sortAttributeName="code"
                                  handleValueChange={(val: any) => {
                                    return this.handleSupplierValueChange(val);
                                  }}
                                  filterAttributes={[ 'code', 'name' ]}
                                  primaryAtr="code"
                                  secondaryAtr="name"
                                />
                              )}
                            />
                          )}
                      </div>
                    </div>
                  </div>
                  <div className="row mt-4">
                    <div className="col-12 text-right">
                      <button type="button" className="btn btn-outline-secondary btn-lg mr-2"
                              onClick={this.onCancel}>
                        {t('b.cancel_Generic cancel button translation').toUpperCase()}</button>
                      <button type="submit" className="btn btn-primary btn-lg" onClick={this.onSubmit}>
                        {t('b.save_Generic save button translation').toUpperCase()}
                      </button>
                    </div>
                  </div>
                </>
              ) : ''}
            </form>
          );
        }}
      />
    ) : '');
  };

  render() {
    const { t } = this.props;
    const showForm = this.props.isNew || this.state.isEdit;
    return (
      <main>
        <Breadcrumbs
          items={
            [
              { path: '/admin', label: 'Admin' },
              { path: '/admin/notifications', label: 'Notifications' },
              {
                path: null,
                label: this.props.isNew ? 'New' : (this.props.selectedNotification?.title ?? '')
              }
            ]
          }
        />
        <div className="page-content">
          <div className="row justify-content-end">
            <div className="col-12 d-flex align-items-center justify-content-between">
              <h1
                className="mb-0">{`${!this.props.isNew && this.props.selectedNotification ? this.props.selectedNotification.title : t('page_title.new_notification')}`}</h1>
            </div>
          </div>
          {this.props.user && !showForm && userCanAccessComponent(this.props.user, adminModuleEditRoles) ? (
            <SectionTitle
              title={t('section_title.notification.notification').toUpperCase()}
              showButton
              buttonName={t('b.edit_Generic edit button').toUpperCase()}
              onEditClick={() => this.setState({ isEdit: true })}
              showDeleteButton
              deleteButtonName={t('b.delete_Generic delete button').toUpperCase()}
              onDeleteClick={() => this.setState({ isModalOpen: true })}
            />
          ) : (
            <SectionTitle
              title={t('section_title.notification.notification').toUpperCase()}
            />
          )}
          {this.getDetailView(showForm)}
          {this.getEditView(showForm)}
        </div>
        <ConfirmationModal
          title={t('modal_text.promo_code.confirm_removal.title')}
          content={t('modal_text.promo_code.confirm_removal.content')}
          isOpen={this.state.isModalOpen}
          toggle={() => this.setState({ isModalOpen: false })}
          onConfirm={() => this.onDeleteConfirm()}
          onCancel={() => this.setState({ isModalOpen: false })}
        />
      </main>
    );
  }

}

const mapStateToProps = (state: AppState) => ({
  selectedNotification: state.admin.notifications.selectedNotification,
  postSuccess: state.admin.notifications.postSuccess,
  lastFetch: state.admin.notifications.lastFetch,
  user: state.authentication.user,
  supplier: state.authentication.activeSupplier,
  supplierList: state.authentication.suppliers
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<{}, {}, NotificationActionTypes>
) => ({
  getNotification: async (id: number) => dispatch(getNotification(id)),
  addNotification: (request: NotificationRequestModel) => dispatch(addNewNotification(request)),
  editNotification: (request: NotificationRequestModel, id: number) => dispatch(editNotification(request, id)),
  deleteNotification: (id: number) => dispatch(deleteNotification(id))
});

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(NotificationDetails));